/**
 * feedback.ts —— 失败回传与双轮反馈（D10）
 *
 * 设计文档 2.6 定的流程：
 *
 *   第 1 轮：用户话 → AI → IL → 执行
 *     · 成功了 → 本地拼接 successText，**结束**
 *     · 失败了 → 把真实错误回喂 AI
 *   第 2 轮：AI 看到真实错误 → 输出 explain('解释+建议')
 *     · 再失败  → **本地渲染**，不再回喂
 *
 * 为什么要设「第 2 轮再失败就本地渲染」这道闸：
 *   模型可能反复给出同样的错误指令。如果无脑回喂，
 *   就会形成「AI 生成 → 失败 → 回喂 → AI 生成 → 失败」的死循环，
 *   既烧 token 又卡住用户。所以第 2 轮的结果不再执行 ——
 *   它被当成**纯文本**展示（只可能是 explain，因为提示词这么要求）。
 *   万一模型又吐出命令，求值器根本不会被调用，直接被当文字显示。
 *
 * 这个设计让「最多两轮」成为代码结构上的硬约束，而不是靠计数器提心吊胆。
 */

import { chat, AIError } from './client';
import { buildSystemPrompt, buildFeedbackPrompt, buildAnswerPrompt } from './prompt';
import { sanitize } from './sanitize';
import { extractSpoken } from './spoken';
import { trace } from './trace';
import { tryParse } from '@/il/parser';
import { evaluate } from '@/il/evaluator';
import { describeCapabilities, describeHandles, handles } from '@/il/register';
import { summarizeDigest } from './summarize';
import { eventBus } from '@/core/event-bus';
import type { EvalResult } from '@/types/il';

/** 一次完整交互的最终结果，供 UI 展示 */
export interface InteractionResult {
  /** 用户原话 */
  input: string;
  /** 最终给用户看的文字 */
  text: string;
  /** 所有执行结果（第 1 轮的全部 + 可能的第 2 轮） */
  results: EvalResult[];
  /** 是否走了失败二轮 */
  usedFeedback: boolean;
  /** 模型第 1 轮的**原文**（未经清洗）。调试模式要把它原样打到控制台 */
  raw?: string;
  /** 清洗后的 IL（第 1 轮）。AI 真正"说了什么指令"看这个 */
  code?: string;
  /** 是否走了「回答轮」（纯查询 → 回喂数据 → AI 组织语言） */
  usedAnswer?: boolean;
}

/**
 * 把「模型这一轮到底说了什么」广播出去。
 *
 * 为什么要单独立一个函数而不是就地 console.debug：
 *   ① 控制台输出是**需求**（用户明确要求看到原始响应），
 *      但输出的时机与格式应该只有一处定义；
 *   ② 调试页（lx-debug-view）订阅同一个事件来维护「调用日志」列表 ——
 *      一处发射，两处消费，不会出现「控制台有、调试页没有」的不一致；
 *   ③ 调试模式开关的判断在订阅方（UI 层）做，AI 层不关心开关状态 ——
 *      这一层只负责「如实上报发生了什么」，符合 AI 与 UI 解耦的原则。
 *
 * @param input 用户原话
 * @param raw   模型返回的原文
 * @param code  清洗后的 IL
 */
function reportModelResponse(input: string, raw: string, code: string): void {
  // 原文为空（模型给了个空 content）时不发事件 —— 那没有信息量，
  // 只会让调试页多出一条空记录。
  if (!raw && !code) return;
  eventBus.emit('ai:response', { input, raw, code });
}

/**
 * 处理一次用户输入 —— 这是 AI 侧对外的**唯一**入口。
 *
 * 完整链路：自然语言 → 模型 → 清洗 → 解析 → 执行 → （失败则二轮）→ 结果
 */
export async function handleUserInput(
  input: string,
  options: { onConfirm?: (text: string) => Promise<boolean> } = {},
): Promise<InteractionResult> {
  // 开一个控制台分组，把这一轮的所有输出圈在一起（调试模式关闭时是空操作）
  trace.beginTurn(input);

  try {
    // ── 第 1 轮：翻译 ──
    const systemPrompt = buildSystemPromptLocal();
    trace.prompt('第 1 轮 · 翻译', systemPrompt, input);
    const raw1 = await chat(systemPrompt, input);
    const { code } = sanitize(raw1);

    // 如实上报模型这一轮说了什么（控制台 / 调试页都在等这个）
    reportModelResponse(input, raw1, code);
    trace.modelRaw('第 1 轮 · 翻译', raw1, code);

    // 模型没给出任何指令（只说了废话，清洗后为空）
    if (!code) {
      const text = '我没能把这句理解为一条可执行的指令。换个说法试试？';
      trace.outcome('无指令', text);
      return {
        input,
        text,
        results: [],
        usedFeedback: false,
        raw: raw1,
        code,
      };
    }

    // ── 解析 ──
    const parsed = tryParse(code);
    if (!parsed.ok) {
      // 语法错误的处理策略与运行时错误不同：
      // 语法错误说明**模型没写好**，值得回喂让它重写一版干净的
      trace.parseError(code, parsed.error.kind, parsed.error.message);
      return await runFeedback(input, code, formatError(parsed.error), [], options, raw1);
    }

    // ── 执行 ──
    const results = await evaluate(parsed.program, { onConfirm: options.onConfirm });
    trace.evalResults(results);

    const failures = results.filter((r) => !r.ok);
    if (failures.length === 0) {
      // 全部成功。
      // 先判断这一轮是不是「纯查询」—— 用户问的是问题，模型取的是数据。
      // 如果是，数据还躺在 results[].value 里没给用户看，得走回答轮。
      if (isPureQuery(results)) {
        return await runAnswer(input, code, results, raw1);
      }
      // 否则是操作指令 —— 本地拼接文案 + 补做「整合文件」的第三段 AI 摘要
      const text = await finalizeSuccesses(results);
      trace.outcome('成功', text);
      return {
        input,
        text,
        results,
        usedFeedback: false,
        raw: raw1,
        code,
      };
    }

    // ── 失败：回传真实错误，进入第 2 轮 ──
    const errorText = failures
      .map((r) => `「${r.command}」失败：${r.error?.message ?? '未知错误'}`)
      .join('\n');
    trace.feedback('失败二轮 · 回喂真实错误', errorText);

    return await runFeedback(input, code, errorText, results, options, raw1);
  } finally {
    // 无论走哪条路径（含抛异常），分组都要收掉，否则控制台会一直缩进
    trace.endTurn();
  }
}

/**
 * 判断这一轮是不是「纯查询」。
 *
 * 条件：每一条成功的语句都命中了 [查] 方法，且没有任何一条真正改了状态。
 * 为什么要「每一条」都查 —— 因为混合指令（「切主题 + 顺便看看有哪些联系人」）
 * 应该先执行动作、由本地文案回答动作部分；这时候用数据回喂反而会让回答跑偏。
 * 纯查询才好办：用户就是在问，给一段正确的答案就完事了。
 */
function isPureQuery(results: EvalResult[]): boolean {
  if (results.length === 0) return false;
  return results.every((r) => r.ok && r.isQuery);
}

/**
 * 「回答轮」：把真实取到的数据回喂给模型，让它用自然语言回答用户。
 *
 * 与失败二轮共用一条原则 —— **这一轮的产出同样不执行**，
 * 只当纯文本展示（应该是 explain('...')）。
 *
 * @param code    模型上一轮给出的查询指令（原样回喂，便于它对应上下文）
 * @param results 已执行的结果，value 里是真实数据
 * @param raw1    第 1 轮模型原文，透传给 UI（调试模式要打控制台）
 */
async function runAnswer(
  input: string,
  code: string,
  results: EvalResult[],
  raw1 = '',
): Promise<InteractionResult> {
  const dataText = renderQueryData(results);

  try {
    const { systemPrompt, userMessage } = buildAnswerPrompt(
      describeCapabilities(),
      code,
      dataText,
      describeHandles(),
    );
    trace.feedback('回答轮 · 回喂真实数据', dataText);
    trace.prompt('第 2 轮 · 回答', systemPrompt, userMessage);
    const raw2 = await chat(systemPrompt, userMessage);
    const { code: text } = sanitize(raw2);

    // 这一轮是「组织语言」而不是「翻译指令」，原文同样值得上报 ——
    // 排查「AI 为什么答歪了」时，看的正是它这句话。
    reportModelResponse(input, raw2, text);
    trace.modelRaw('第 2 轮 · 回答', raw2, text);

    // 第 2 轮的产出按约定是 explain('…')。这里**只取它要说的那句话**，
    // 而不是把 explain(...) 原文甩给用户 —— 后者会泄露 IL 这个内部机制。
    // 注意 extractSpoken 不做任何执行，纯字符串切分（见 spoken.ts）。
    const spoken = extractSpoken(text);

    // 取不到话（模型没给 explain、或给了空文本）就退回本地渲染的真实数据，
    // 至少让用户看到内容，而不是一片空白。
    const finalText = spoken || dataText;
    trace.outcome('回答轮', finalText);

    return {
      input,
      text: finalText,
      results,
      usedFeedback: true,
      usedAnswer: true,
      raw: raw1,
      code,
    };
  } catch (err) {
    // 回答轮调用失败 —— 本地渲染兜底，数据反正是真的，直接给用户看
    const reason = err instanceof AIError ? err.reason : String(err);
    const finalText = `${dataText}\n\n（尝试让 AI 组织这段话时出错：${reason}）`;
    trace.note(`回答轮调用失败：${reason}`);
    trace.outcome('回答轮', finalText);
    return {
      input,
      text: finalText,
      results,
      usedFeedback: true,
      usedAnswer: true,
      raw: raw1,
      code,
    };
  }
}

/**
 * 把查询结果渲染成给模型看的文本。
 *
 * 刻意保持**贴近原始数据**，不做美化 —— 因为这一步的产物是「原料」，
 * 组织成人话是回答轮里 AI 的活。这里只保证：
 *   · 结构清楚（每条指令一行标题 + 一段 JSON）
 *   · 不丢信息（JSON 序列化，模型能读懂嵌套）
 */
function renderQueryData(results: EvalResult[]): string {
  const blocks: string[] = [];
  for (const r of results) {
    blocks.push(`【${r.command}】`);
    blocks.push(safeJson(r.value));
  }
  return blocks.join('\n');
}

/** 序列化查询数据。循环引用或超大数据不该把整条链路搞崩 */
function safeJson(value: unknown): string {
  if (value === undefined) return '（没有数据）';
  try {
    const text = JSON.stringify(value, null, 2);
    // 极端情况下截断，避免把提示词撑爆 —— 但要说清楚被截了
    return text.length > 4000 ? `${text.slice(0, 4000)}\n…（数据过长，已截断）` : text;
  } catch {
    return String(value);
  }
}

/**
 * 成功路径的收尾。
 *
 * 大部分指令的成功文案在求值时就由 successText 拼好了，这里只做一件事：
 * **补做「整合文件」的第 ③ 段（AI 摘要）** —— 因为那一步需要调 AI，
 * 放在求值器里不合适（求值器不该依赖 AI）。
 *
 * 判断「哪条结果需要补摘要」的方式：看这条命令里有没有 file.summarize。
 * 有的话，从 handles 里取最新登记的 digest 句柄，生成摘要并把这段
 * 摘要文字**追加**到该条的成功文案后面 —— 这样用户看到的就是
 * 「已整理王二的 12 个文件」+ 一段人话摘要。
 *
 * 摘要失败不改变 ok 状态：清单已经落库了，只是描述那句话没生成，
 * 如实告诉用户"摘要暂时没生成"，而不是假装整件事失败。
 */
async function finalizeSuccesses(results: EvalResult[]): Promise<string> {
  const lines: string[] = [];

  for (const r of results) {
    let line = r.desc;

    if (r.ok && /file\s*\.\s*summarize/.test(r.command)) {
      const latest = handles.latest('digest');
      const digestId = (latest?.payload as { digestId?: string } | undefined)?.digestId;
      if (digestId) {
        const s = await summarizeDigest(digestId);
        line += s.ok
          ? `\n\n${s.summary}`
          : `\n\n（文件清单已经存好了，但生成摘要时出了点问题：${s.reason ?? '未知原因'}。之后可以说「把那份摘要补上」。）`;
      }
    }

    lines.push(line);
  }

  return lines.join('\n');
}

/**
 * 第 2 轮：把真实错误回喂，拿一段解释性文字。
 *
 * 注意这一轮的产出**不再执行**，只当纯文本展示。
 * 所以即使模型不听话吐了命令，也不会真的有动作发生 ——
 * 最多是用户看到一段奇怪的文字，不会造成意外的状态变更。
 */
async function runFeedback(
  input: string,
  failedCode: string,
  errorText: string,
  prevResults: EvalResult[],
  _options: { onConfirm?: (text: string) => Promise<boolean> },
  raw1 = '',
): Promise<InteractionResult> {
  try {
    const { systemPrompt, userMessage } = buildFeedbackPrompt(
      describeCapabilities(),
      failedCode,
      errorText,
      describeHandles(),
    );
    trace.prompt('第 2 轮 · 失败解释', systemPrompt, userMessage);
    const raw2 = await chat(systemPrompt, userMessage);
    const { code: text } = sanitize(raw2);

    reportModelResponse(input, raw2, text);
    trace.modelRaw('第 2 轮 · 失败解释', raw2, text);

    // 同上：这一轮只可能是 explain('…')，取它要说的话，而不是展示 IL 原文。
    const spoken = extractSpoken(text);
    const finalText = spoken || '指令执行失败了，但我没能给出进一步说明。';

    trace.outcome('失败二轮', finalText);

    return {
      input,
      text: finalText,
      results: prevResults,
      usedFeedback: true,
      raw: raw1,
      code: failedCode,
    };
  } catch (err) {
    // 二轮调用本身也失败了（网络问题等）—— 本地渲染兜底，不让用户面对空白
    const reason = err instanceof AIError ? err.reason : String(err);
    const finalText = `${errorText}\n\n（尝试让 AI 解释失败原因时出错：${reason}）`;
    trace.note(`失败二轮调用失败：${reason}`);
    trace.outcome('失败二轮', finalText);
    return {
      input,
      text: finalText,
      results: prevResults,
      usedFeedback: true,
      raw: raw1,
      code: failedCode,
    };
  }
}

/** 提示词在 M3 由 registry 生成 —— 这正是「单一真相源」生效的地方 */
function buildSystemPromptLocal(): string {
  return buildSystemPrompt(describeCapabilities(), describeHandles());
}

function formatError(err: { kind: string; message: string }): string {
  return `${err.kind}：${err.message}`;
}
